import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { LogIn, LogOut, Menu, X, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const LOGO_URL =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_sundsvall-carpool/artifacts/pkv81sdt_Sk%C3%A4rmbild%202026-08-10%20201728.png";

const links = [
  { label: "Så funkar det", href: "#hur" },
  { label: "Bilen", href: "#bilen" },
  { label: "Priser", href: "#priser" },
  { label: "Student", href: "#student" },
  { label: "Kontakt", href: "#kontakt" },
];

export const Nav = ({ content, onJoin }) => {
  const { user, login, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-[#F7F7F4]/80 backdrop-blur-xl border-b border-[#1A3B2A]/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="grid place-items-center h-11 w-11 rounded-full bg-white overflow-hidden border border-[#1A3B2A]/10 shadow-sm transition-transform duration-300 group-hover:scale-105">
            <img src={LOGO_URL} alt="Sundsvalls Bilpolare logotyp" className="h-9 w-9 object-contain" />
          </span>
          <span
            className={`font-display text-xl sm:text-2xl leading-none transition-colors duration-300 ${
              scrolled ? "text-[#1A3B2A]" : "text-[#F7F7F4]"
            }`}
          >
            {content?.brand || "Sundsvalls Bilpolare"}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              className={`text-sm font-medium transition-colors duration-300 ${
                scrolled ? "text-[#1B1F1C]/70 hover:text-[#1A3B2A]" : "text-[#F7F7F4]/85 hover:text-[#F7F7F4]"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-join-btn"
            onClick={onJoin}
            className={`hidden sm:inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-300 ${
              scrolled ? "text-[#1A3B2A] hover:bg-[#1A3B2A]/5" : "text-[#F7F7F4] hover:bg-white/10"
            }`}
          >
            Bli medlem
          </button>

          {user ? (
            <>
              {user.is_admin && (
                <a
                  href="/admin"
                  data-testid="nav-admin-link"
                  className={`hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold ${
                    scrolled ? "text-[#4A6D56]" : "text-[#C2D3C4]"
                  }`}
                >
                  <ShieldCheck size={16} /> Admin
                </a>
              )}
              <button
                data-testid="nav-logout-btn"
                onClick={logout}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold active:scale-95 transition-[filter,transform,background-color] duration-300 ${
                  scrolled ? "bg-[#1A3B2A] text-[#F7F7F4] hover:brightness-125" : "bg-[#F7F7F4] text-[#1A3B2A] hover:bg-[#C2D3C4]"
                }`}
              >
                <LogOut size={16} /> Logga ut
              </button>
            </>
          ) : (
            <button
              data-testid="nav-login-btn"
              onClick={login}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold active:scale-95 transition-[filter,transform,background-color] duration-300 ${
                scrolled ? "bg-[#1A3B2A] text-[#F7F7F4] hover:brightness-125" : "bg-[#F7F7F4] text-[#1A3B2A] hover:bg-[#C2D3C4]"
              }`}
            >
              <LogIn size={16} /> Logga in
            </button>
          )}
          <button
            data-testid="nav-mobile-toggle"
            className={`lg:hidden ${scrolled ? "text-[#1A3B2A]" : "text-[#F7F7F4]"}`}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mt-3 mx-6 rounded-2xl bg-[#F7F7F4] border border-[#D1D8D3] p-4 flex flex-col gap-3">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-sm font-medium text-[#1B1F1C]/80 py-1"
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={() => {
              setOpen(false);
              onJoin();
            }}
            className="text-left text-sm font-semibold text-[#1A3B2A] py-1"
          >
            Bli medlem
          </button>
          {user?.is_admin && (
            <a href="/admin" className="text-sm font-semibold text-[#4A6D56] py-1">
              Admin
            </a>
          )}
        </div>
      )}
    </motion.header>
  );
};
